import { useState, useEffect, useRef } from 'react';
import { ethers } from 'ethers';
import P01Connect from './components/P01Connect';
import Header from './components/Header';
import P02MainIdle from './components/P02MainIdle';
import P03CandidateReg from './components/P03CandidateReg';
import P04VoteManage from './components/P04VoteManage';
import P05Vote from './components/P05Vote';
import P06LiveResult from './components/P06LiveResult';
import P07FinalResult from './components/P07FinalResult';
import VotingABI from './abi/Voting.json';

const CONTRACT_ADDRESS = process.env.REACT_APP_CONTRACT_ADDRESS;
const SEPOLIA_RPC_URL  = process.env.REACT_APP_SEPOLIA_RPC_URL;

function getReadContract() {
  const provider = new ethers.JsonRpcProvider(SEPOLIA_RPC_URL);
  return new ethers.Contract(CONTRACT_ADDRESS, VotingABI.abi, provider);
}

// 화면 ID (문서 3.1 화면 목록)
const Screen = {
  CONNECT:       'P01',
  MAIN_IDLE:     'P02',
  CANDIDATE_REG: 'P03',
  VOTE_MANAGE:   'P04',
  VOTE:          'P05',
  LIVE_RESULT:   'P06',
  FINAL_RESULT:  'P07',
};

export default function App() {
  const [screen, setScreen]               = useState(Screen.CONNECT);
  const [walletAddress, setWalletAddress] = useState(null);
  const [isAdmin, setIsAdmin]             = useState(false);
  const [electionState, setElectionState]   = useState(null);
  const [votedCandidateId, setVotedCandidateId] = useState(null);
  const pollRef     = useRef(null);
  const isAdminRef  = useRef(false);

  useEffect(() => {
    if (!window.ethereum) {
      getReadContract().getState()
        .then(state => { if (state === 2n) setScreen(Screen.FINAL_RESULT); })
        .catch(() => {});
      return;
    }
    window.ethereum.request({ method: 'eth_accounts' }).then((accounts) => {
      if (accounts.length > 0) initAfterConnect(accounts[0]);
    });
    const onAccountsChanged = (accounts) => {
      if (accounts.length === 0) {
        stopPolling();
        isAdminRef.current = false;
        setWalletAddress(null);
        setIsAdmin(false);
        setElectionState(null);
        setScreen(Screen.CONNECT);
      } else {
        initAfterConnect(accounts[0]);
      }
    };
    const onChainChanged = () => window.location.reload();
    window.ethereum.on('accountsChanged', onAccountsChanged);
    window.ethereum.on('chainChanged', onChainChanged);
    return () => {
      window.ethereum.removeListener('accountsChanged', onAccountsChanged);
      window.ethereum.removeListener('chainChanged', onChainChanged);
      stopPolling();
    };
  }, []);

  async function initAfterConnect(address) {
    setWalletAddress(address);
    try {
      const contract  = getReadContract();
      const ownerAddr = await contract.owner();
      const admin     = ownerAddr.toLowerCase() === address.toLowerCase();
      setIsAdmin(admin);
      isAdminRef.current = admin;
      const state = await contract.getState();
      setElectionState(state);
      await routeByState(state, address, admin);
      startPolling(address);
    } catch (e) {
      console.error('초기화 오류:', e);
      setIsAdmin(false);
      isAdminRef.current = false;
      setElectionState(null);
      setScreen(Screen.MAIN_IDLE);
    }
  }

  async function routeByState(state, address, admin = false) {
    if (state === 0n) {
      setScreen(Screen.MAIN_IDLE);
    } else if (state === 1n) {
      if (admin) {
        setScreen(Screen.VOTE_MANAGE);
      } else {
        const contract = getReadContract();
        const voted    = await contract.hasAddressVoted(address);
        setScreen(voted ? Screen.LIVE_RESULT : Screen.VOTE);
      }
    } else {
      stopPolling();
      setScreen(Screen.FINAL_RESULT);
    }
  }

  function startPolling(address) {
    stopPolling();
    pollRef.current = setInterval(async () => {
      try {
        const contract = getReadContract();
        const state    = await contract.getState();
        setElectionState(state);
        if (state === 2n) {
          stopPolling();
          setScreen(Screen.FINAL_RESULT);
        } else if (state === 1n) {
          if (!isAdminRef.current) {
            const voted = await contract.hasAddressVoted(address);
            setScreen(voted ? Screen.LIVE_RESULT : Screen.VOTE);
          }
        }
      } catch (e) {
        console.error('폴링 오류:', e);
      }
    }, 5000);
  }

  function stopPolling() {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }

  async function connectWallet() {
    try {
      const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
      await initAfterConnect(accounts[0]);
    } catch (e) {
      console.error('지갑 연결 오류:', e);
    }
  }

  return (
    <div className="ev-app">
      <Header electionState={electionState} walletAddress={walletAddress} isAdmin={isAdmin} />
      {screen === Screen.CONNECT   && <P01Connect onConnect={connectWallet} />}
      {screen === Screen.MAIN_IDLE && (
        <P02MainIdle
          isAdmin={isAdmin}
          onRegisterClick={() => setScreen(Screen.CANDIDATE_REG)}
          onManageClick={() => setScreen(Screen.VOTE_MANAGE)}
        />
      )}
      {screen === Screen.CANDIDATE_REG && (
        <P03CandidateReg
          isAdmin={isAdmin}
          onBackClick={() => setScreen(Screen.MAIN_IDLE)}
          onManageClick={() => setScreen(Screen.VOTE_MANAGE)}
        />
      )}
      {screen === Screen.VOTE_MANAGE && (
        <P04VoteManage
          isAdmin={isAdmin}
          onBackClick={() => setScreen(Screen.MAIN_IDLE)}
          onFinishClick={() => setScreen(Screen.FINAL_RESULT)}
          onVoteClick={() => setScreen(Screen.VOTE)}
        />
      )}
      {screen === Screen.VOTE && (
        <P05Vote
          walletAddress={walletAddress}
          onVotedClick={(id) => { setVotedCandidateId(id); setScreen(isAdmin ? Screen.VOTE_MANAGE : Screen.LIVE_RESULT); }}
          onEndedClick={() => setScreen(Screen.FINAL_RESULT)}
        />
      )}
      {screen === Screen.LIVE_RESULT && (
        <P06LiveResult
          votedCandidateId={votedCandidateId}
          onEndedClick={() => setScreen(Screen.FINAL_RESULT)}
        />
      )}
      {screen === Screen.FINAL_RESULT && <P07FinalResult isAdmin={isAdmin} />}
    </div>
  );
}
